import { useCallback, useMemo, useState } from 'react';
import { Table, Dropdown, ToggleTabs, TypographyWeights } from '@carboledger/ui';
import { CellText, SummaryTiles } from '@/components/common';
import { buildTableLabels } from '@/config/tableLabels';
import { UNIT_TYPES } from '@/config/unitTypes.constants';
import { useTranslations } from '@/hooks';
import { EMPTY_LIST } from '@/lib/utils';
import { useGetMaterialMovementSummaryQuery } from '../api/inventoryApi';
import { MOVEMENT_VIEWS } from '../constants/inventory.constants';
import { buildSummaryEmptyState, buildSummaryNoResults } from '../constants/inventoryEmptyStates';
import { buildSummaryFilters } from '../constants/summaryFilters';
import {
  buildMovementSummaryColumns,
  buildMovementDetailedColumns,
} from '../constants/summaryColumns';
import { MOVEMENT_SECTIONS } from '../mappers/inventoryReportMappers';
import { buildMovementSummaryView, buildMovementDetailedView } from '../utils/summaryReports';
import styles from './MovementSummaryView.module.scss';

const getRowId = (row) => row.id;
const getRowSection = (row) => row.section;

const EMPTY_STATE_KEYS = {
  titleKey: 'INVENTORY_MOVEMENT_EMPTY_TITLE',
  descriptionKey: 'INVENTORY_MOVEMENT_EMPTY_DESCRIPTION',
};

// A subtotal / Total row's cells: the period figures as they are, the grand total in bold.
const buildTotalCells = (periods, figures) => ({
  ...Object.fromEntries(
    periods.map((period) => [period.id, <CellText key={period.id}>{figures[period.id]}</CellText>]),
  ),
  grandTotal: <CellText weight={TypographyWeights.semibold}>{figures.grandTotal}</CellText>,
});

/**
 * Props:
 *   header     the sub-tab strip
 *   dateRange  the From – To range, drawn first in the table's toolbar
 *   filtered   the range differs from the default, so an empty result is "no results"
 *   args       the report's variables; null while the range is invalid
 *   units      { declaration, working } unit symbols of this material
 */
export const MovementSummaryView = ({ header, dateRange, filtered, args, units }) => {
  const t = useTranslations();
  const [view, setView] = useState(MOVEMENT_VIEWS.detailed);
  const [unitType, setUnitType] = useState(UNIT_TYPES.declaration);
  const [filters, setFilters] = useState({});
  const { data: report, isFetching } = useGetMaterialMovementSummaryQuery(args, { skip: !args });

  const isDetailed = view === MOVEMENT_VIEWS.detailed;

  const viewOptions = useMemo(
    () => [
      { value: MOVEMENT_VIEWS.detailed, label: t('INVENTORY_VIEW_DETAILED') },
      { value: MOVEMENT_VIEWS.summary, label: t('INVENTORY_VIEW_SUMMARY') },
    ],
    [t],
  );

  // Figma: declaration first, then tracking (when the batches agree on one), then working.
  const unitSymbols = useMemo(
    () => ({
      [UNIT_TYPES.declaration]: units?.declaration ?? '',
      [UNIT_TYPES.tracking]: report?.trackingUnit ?? '',
      [UNIT_TYPES.working]: units?.working ?? '',
    }),
    [units, report?.trackingUnit],
  );
  const unitOptions = useMemo(
    () =>
      [
        { value: UNIT_TYPES.declaration, badgeText: t('UNIT_DECLARATION') },
        { value: UNIT_TYPES.tracking, badgeText: t('UNIT_TRACKING') },
        { value: UNIT_TYPES.working, badgeText: t('UNIT_WORKING') },
      ]
        .filter((option) => unitSymbols[option.value])
        .map((option) => ({ ...option, label: unitSymbols[option.value] })),
    [t, unitSymbols],
  );
  // A unit the new report no longer offers (e.g. tracking went into conflict) falls back.
  const activeUnitType = unitSymbols[unitType] ? unitType : UNIT_TYPES.declaration;

  const handleViewChange = useCallback((value) => setView(value), []);
  const handleUnitChange = useCallback((event) => setUnitType(event.target.value), []);
  const handleFilterChange = useCallback((values) => setFilters(values), []);

  const summary = useMemo(
    () => buildMovementSummaryView(report, activeUnitType, filters),
    [report, activeUnitType, filters],
  );
  const detailedRows = useMemo(
    () => buildMovementDetailedView(report, activeUnitType, filters),
    [report, activeUnitType, filters],
  );

  const columns = useMemo(
    () =>
      isDetailed
        ? buildMovementDetailedColumns(t, { unit: unitSymbols[activeUnitType] })
        : buildMovementSummaryColumns(t, { periods: summary.periods }),
    [isDetailed, t, unitSymbols, activeUnitType, summary.periods],
  );

  const groups = useMemo(
    () => [
      {
        id: MOVEMENT_SECTIONS.inbound,
        subtotal: {
          label: t('INVENTORY_INBOUND_SUBTOTAL'),
          info: t('INVENTORY_INBOUND_SUBTOTAL_INFO'),
          cells: buildTotalCells(summary.periods, summary.subtotals[MOVEMENT_SECTIONS.inbound]),
        },
      },
      {
        id: MOVEMENT_SECTIONS.outbound,
        subtotal: {
          label: t('INVENTORY_OUTBOUND_SUBTOTAL'),
          info: t('INVENTORY_OUTBOUND_SUBTOTAL_INFO'),
          cells: buildTotalCells(summary.periods, summary.subtotals[MOVEMENT_SECTIONS.outbound]),
        },
      },
    ],
    [t, summary],
  );
  const footerRow = useMemo(
    () => buildTotalCells(summary.periods, summary.total),
    [summary.periods, summary.total],
  );

  const filterDefs = useMemo(
    () => buildSummaryFilters(t, isDetailed ? report?.detailed : report?.rows),
    [t, isDetailed, report],
  );
  const tableLabels = useMemo(() => buildTableLabels(t), [t]);
  const emptyState = useMemo(() => buildSummaryEmptyState(t, EMPTY_STATE_KEYS), [t]);
  const noResults = useMemo(() => buildSummaryNoResults(t), [t]);

  const toolbarLeft = useMemo(
    () => (
      <>
        {dateRange}
        <Dropdown
          appearance="filled"
          className={styles.unitPicker}
          value={activeUnitType}
          onChange={handleUnitChange}
          options={unitOptions}
          aria-label={t('INVENTORY_UNIT_PICKER')}
        />
      </>
    ),
    [dateRange, activeUnitType, handleUnitChange, unitOptions, t],
  );
  const toolbarRight = useMemo(
    () => (
      <ToggleTabs
        options={viewOptions}
        value={view}
        onChange={handleViewChange}
        aria-label={t('INVENTORY_SUMMARY_TAB_MOVEMENT')}
      />
    ),
    [viewOptions, view, handleViewChange, t],
  );

  return (
    <SummaryTiles header={header}>
      <Table
        columns={columns}
        data={isDetailed ? detailedRows : summary.rows}
        loading={isFetching}
        getRowId={getRowId}
        groups={isDetailed ? EMPTY_LIST : groups}
        getRowGroup={isDetailed ? undefined : getRowSection}
        footerRow={isDetailed ? undefined : footerRow}
        footerLabel={t('INVENTORY_TOTAL_ROW')}
        filters={filterDefs}
        onFilterChange={handleFilterChange}
        toolbarLeft={toolbarLeft}
        toolbarRight={toolbarRight}
        filtered={filtered}
        showPagination={false}
        emptyState={emptyState}
        noResults={noResults}
        labels={tableLabels}
      />
    </SummaryTiles>
  );
};
